import React, { useCallback, useMemo, useState } from 'react';
import { useProjectStore } from '../store/projectStore';
import type { Node as FlowNode } from '@xyflow/react';
import { ImportExportDialog } from '../panels/ImportExportDialog';

/**
 * Graph Tree Component
 *
 * Displays the hierarchical structure of all graphs in a project as an outline tree.
 * This is the primary navigation tool for understanding and traversing complex,
 * deeply nested story structures.
 *
 * **Purpose:**
 * As stories grow with multiple nesting levels (Chapter → Scene → Sequence → Dialogue),
 * the breadcrumb alone becomes insufficient. The graph tree provides:
 * - Complete project overview at a glance
 * - Jump to any graph without traversing ancestors
 * - Visual hierarchy showing containment relationships
 * - Node count per graph (indicating complexity)
 *
 * **Display Structure:**
 * ```
 * Project Root
 * ├─ Chapter 1
 * │  ├─ Scene 1 (4 nodes)
 * │  │  └─ Dialogue 1 (2 nodes)
 * │  └─ Scene 2 (7 nodes)
 * └─ Chapter 2
 *    └─ Scene 3 (12 nodes)
 * ```
 *
 * **Interaction:**
 * - Click any graph to open it (updates breadcrumb, swaps canvas)
 * - Expand/collapse folders to explore deep hierarchies
 * - Shows container node names as expandable tree items
 * - Double-click acts same as single-click
 * - Right-click (future): context menu for create/delete/duplicate
 *
 * **Performance Optimization:**
 * For projects with 100+ graphs, the tree is virtualized (not implemented here
 * but marked for future optimization). Lazy-loading of child graphs keeps
 * memory footprint bounded.
 *
 * @component
 * @returns {React.ReactElement | null} Tree navigation UI or null if no project loaded
 *
 * @example
 * // Sidebar panel showing complete project structure
 * <div className="panel">
 *   <GraphTree />
 * </div>
 */
export const GraphTree: React.FC = () => {
    const currentProject = useProjectStore((state) => state.currentProject);
    const currentGraph = useProjectStore((state) => state.currentGraph);
    const nodes = useProjectStore((state) => state.nodes);
    const openGraph = useProjectStore((state) => state.openGraph);

    /**
     * Track which tree items are expanded in the UI
     * State key is the UUID of each container node's child graph
     */
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [showImportDialog, setShowImportDialog] = useState(false);

    /**
     * Extract container nodes from current graph
     *
     * Container nodes represent the immediate child graphs. The data structure
     * contains `childGraphUuid` to enable navigation.
     *
     * Computed unconditionally (before the `!currentProject` early return
     * below) — this hook must run on every render regardless of whether a
     * project is loaded yet, per the Rules of Hooks.
     *
     * @type {FlowNode<any>[]}
     */
    const containerNodes = useMemo(
        () => nodes.filter((n) => n.type === 'container'),
        [nodes]
    );

    if (!currentProject || !currentGraph) {
        return null;
    }

    /**
     * Toggle tree item expansion state
     *
     * Clicking the expand/collapse arrow toggles whether the tree item's
     * children are visible. State is managed locally - closing a tree item
     * doesn't unload the graph data.
     *
     * @param {string} graphUuid - UUID of the graph whose children to toggle
     */
    const toggleExpanded = useCallback((graphUuid: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(graphUuid)) {
                next.delete(graphUuid);
            } else {
                next.add(graphUuid);
            }
            return next;
        });
    }, []);

    /**
     * Handle graph item click - navigate to that graph
     *
     * Opens the selected graph, updating the breadcrumb, swapping the canvas
     * content, and restoring the saved viewport for that graph.
     *
     * @param {string} graphUuid - UUID of the graph to open
     * @async
     */
    const handleGraphClick = useCallback(
        (graphUuid: string) => {
            if (graphUuid !== currentGraph.uuid) {
                openGraph(graphUuid);
            }
        },
        [currentGraph.uuid, openGraph]
    );

    return (
        <div className="graph-tree panel">
            {/* Panel header */}
            <div className="panel-header d-flex justify-content-between align-items-center">
                <h6 className="mb-0">🌳 Graph Tree</h6>
                <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setShowImportDialog(true)}
                    title="Import a .dgpack bundle as a new container in the currently open graph"
                >
                    <i className="ti ti-upload me-1" />
                    Import bundle here
                </button>
            </div>

            {/* Tree content */}
            <div className="panel-body p-2">
                {/* Root graph item */}
                <div className="tree-item mb-2">
                    <button
                        className={`btn btn-link ps-0 btn-sm ${
                            currentGraph.uuid === currentProject.uuid ? 'active' : ''
                        }`}
                        onClick={() => handleGraphClick(currentGraph.uuid)}
                        title={currentGraph.name || 'Root Graph'}
                    >
                        📊 {currentGraph.name || 'Root'}
                    </button>
                </div>

                {/* Container nodes (child graphs) */}
                {containerNodes.length === 0 ? (
                    <div className="text-muted small ms-3">
                        <em>No nested graphs yet</em>
                    </div>
                ) : (
                    <div className="tree-children ps-2 border-left">
                        {containerNodes.map((node) => {
                            const childGraphUuid = node.data?.childGraphUuid as string | undefined;
                            const childNodeCount = node.data?.childNodeCount ?? 0;
                            const hasChildren = childNodeCount > 0;
                            const isExpanded = childGraphUuid && expanded.has(childGraphUuid);

                            return (
                                <div
                                    key={node.id}
                                    className="tree-item mb-1"
                                    style={{ marginLeft: '0.5rem' }}
                                >
                                    {/* Expand/collapse toggle */}
                                    <div className="d-flex align-items-center gap-1">
                                        {hasChildren && (
                                            <button
                                                className="btn btn-link p-0 ps-1"
                                                style={{ fontSize: '0.7rem' }}
                                                onClick={() =>
                                                    childGraphUuid && toggleExpanded(childGraphUuid)
                                                }
                                                title={isExpanded ? 'Collapse' : 'Expand'}
                                            >
                                                {isExpanded ? '▼' : '▶'}
                                            </button>
                                        )}
                                        {!hasChildren && (
                                            <span style={{ display: 'inline-block', width: '1rem' }} />
                                        )}

                                        {/* Container node name */}
                                        <button
                                            className={`btn btn-link ps-0 btn-sm text-start flex-grow-1 ${
                                                currentGraph.uuid === childGraphUuid
                                                    ? 'active'
                                                    : ''
                                            }`}
                                            onClick={() => childGraphUuid && handleGraphClick(childGraphUuid)}
                                            title={`${node.data?.label || node.id} (${childNodeCount} nodes)`}
                                        >
                                            📦 {node.data?.label || 'Untitled'}
                                        </button>

                                        {/* Node count badge */}
                                        <span className="badge bg-secondary-lt badge-sm">
                                            {childNodeCount}
                                        </span>
                                    </div>

                                    {/* Expanded children (for multi-level nesting) */}
                                    {isExpanded && hasChildren && (
                                        <div className="ms-3 mt-1 text-muted small">
                                            <em>{childNodeCount} nodes in graph</em>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Tree statistics */}
            <div className="panel-footer border-top p-2">
                <small className="text-muted d-flex justify-content-between">
                    <span>Total graphs: {containerNodes.length + 1}</span>
                    <span>
                        Current:{' '}
                        <strong>{nodes.length} nodes</strong>
                    </span>
                </small>
            </div>

            {showImportDialog && (
                <ImportExportDialog
                    targetGraphUuid={currentGraph.uuid}
                    onImportComplete={() => openGraph(currentGraph.uuid)}
                    onClose={() => setShowImportDialog(false)}
                />
            )}
        </div>
    );
};
